import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import LearnShell, { useBreakpoint } from "../components/learn/shell/LearnShell";
import LearnImage from "../components/learn/media/LearnImage";
import { LEARN, stickerButton, usePrefersReducedMotion } from "../components/learn/theme";
import { LEARN_UI } from "../components/learn/media/learnAssets";
import useLearnProgress from "../hooks/useLearnProgress";
import { LETTERS } from "../utils/learnLetterProgress";
import { COUNTING_ACTIVITY_IDS } from "../data/countingLadderCatalog";
import { WRITING_TOWN_IDS } from "../data/writingTownCatalog";

/**
 * Pixley Learn hub — a real desktop page: hero, "pick up where you left off",
 * and the three courses side by side with live progress. Stacks on phones.
 */
export default function LearnPage() {
  const navigate = useNavigate();
  const bp = useBreakpoint();
  const reduceMotion = usePrefersReducedMotion();
  const stacked = bp === "xs" || bp === "sm";
  const p = useLearnProgress();

  const lettersDone = p.completedLetterCount || 0;
  const countingDone = p.countingSet?.size || 0;
  const countingTotal = COUNTING_ACTIVITY_IDS.size;
  const writingDone = p.writingDone || 0;
  const writingTotal = WRITING_TOWN_IDS.length;

  const courses = [
    {
      id: "letters",
      title: "Letters",
      blurb: "Trace, sort and play your way through the alphabet — one world at a time.",
      art: `alphabet_world_${p.currentWorld?.id || "garden"}`,
      badge: "badge_letters",
      done: lettersDone,
      total: LETTERS.length,
      next: p.lettersFinished ? "All 26 letters done!" : `Letter ${p.nextLetter} · ${p.currentWorld?.title || "Garden World"}`,
      route: "/learn/letters",
      theme: LEARN.course.letters,
    },
    {
      id: "counting",
      title: "Counting",
      blurb: "Count, compare and build numbers with pieces you can pick up and move.",
      art: "counting_ladder_world_bg",
      badge: "badge_counting",
      done: countingDone,
      total: countingTotal,
      next: p.countingFinished ? "Every number counted!" : p.nextLesson ? `Lesson ${p.nextLesson.number}: ${p.nextLesson.title || "First Numbers"}` : "Start with First Numbers",
      route: "/learn/counting",
      theme: LEARN.course.counting,
    },
    {
      id: "writing",
      title: "Writing",
      blurb: "From first strokes to your very own story, up the path through Writing Town.",
      art: "writing_town_path_bg",
      badge: "badge_writing",
      done: writingDone,
      total: writingTotal,
      next: p.writingFinished ? "Author Studio unlocked!" : p.nextWritingLesson ? `Stop ${p.nextWritingLesson.number} in Writing Town` : "Start at the first stop",
      route: "/learn/writing",
      theme: LEARN.course.writing,
    },
  ];

  const resume = courses.find((c) => c.done > 0 && c.done < c.total) || courses[0];
  const totalDone = lettersDone + countingDone + writingDone;

  return (
    <LearnShell maxWidth={1240}>
      {/* Hero */}
      <motion.section
        initial={reduceMotion ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: "relative", overflow: "hidden", borderRadius: LEARN.radius.xl, background: "#0065F8",
          boxShadow: "0px 6px 0px rgba(7,59,142,0.35), 0px 18px 36px rgba(0,101,248,0.22)",
          color: "#fff", minHeight: stacked ? undefined : 360, display: "flex", alignItems: stacked ? "flex-start" : "center",
        }}
      >
        {stacked ? (
          // Phones: the scene is a band under the text (Pip fully visible), blended in at its top edge.
          <>
            <LearnImage asset="ui/hero_learn_wide" fit="cover" eager style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 250 }} imgStyle={{ objectPosition: "90% 100%" }} />
            <div aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: 190, height: 70, background: "linear-gradient(180deg, #0065F8 0%, rgba(0,101,248,0) 100%)" }} />
          </>
        ) : (
          <>
            <LearnImage asset="ui/hero_learn_wide" fit="cover" eager style={{ position: "absolute", inset: 0 }} imgStyle={{ objectPosition: "right center" }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(0,101,248,0.92) 0%, rgba(0,101,248,0.78) 30%, rgba(0,101,248,0.28) 48%, rgba(0,101,248,0) 62%)" }} />
          </>
        )}
        <div style={{ position: "relative", zIndex: 2, padding: stacked ? "26px 22px 256px" : "44px 46px", maxWidth: 600 }}>
          <span style={{ display: "inline-block", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.35)", padding: "6px 12px", borderRadius: 999 }}>
            Pixley Learn
          </span>
          <h1 style={{ margin: "14px 0 10px", fontSize: "clamp(2rem, 4.2vw, 3.1rem)", fontWeight: 700, lineHeight: 1.02, letterSpacing: "-0.02em", textWrap: "balance", textShadow: "0 2px 12px rgba(7,59,142,0.25)" }}>
            Letters, numbers and stories — learned by playing.
          </h1>
          <p style={{ margin: 0, maxWidth: 440, fontSize: 16.5, opacity: 0.95, lineHeight: 1.5 }}>
            Three worlds built for little hands. Every activity is a game, every game is a step, and Pip cheers the whole way.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => navigate(resume.route)}
              style={stickerButton({ bg: "#fff", deep: "rgba(7,59,142,0.55)", color: LEARN.color.blue, size: "lg" })}
            >
              {resume.done > 0 ? `Continue ${resume.title}` : "Start with Letters"}
            </button>
            {resume.done > 0 && <span style={{ fontSize: 14, opacity: 0.92 }}>Up next: {resume.next}</span>}
          </div>
        </div>
      </motion.section>

      {/* Courses */}
      <section style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Choose a world</h2>
          <span style={{ fontSize: 14, color: LEARN.color.muted }}>{totalDone} {totalDone === 1 ? "activity" : "activities"} finished</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: stacked ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 18 }}>
          {courses.map((c, i) => (
            <CourseCard key={c.id} course={c} index={i} onOpen={() => navigate(c.route)} reduceMotion={reduceMotion} stacked={stacked} />
          ))}
        </div>
      </section>

      {(p.error || p.offline) && (
        <div role="status" style={{ marginTop: 18, background: "#FFF6DE", border: "1.5px solid #F0C25A", borderRadius: 14, padding: "12px 16px", fontSize: 14, color: "#6b4e0a" }}>
          {p.offline ? "You're offline — progress is saved on this device and will sync when you're back." : "We couldn't reach Pixley to load progress. You can keep playing; we'll keep retrying."}
        </div>
      )}

    </LearnShell>
  );
}

function CourseCard({ course: c, index, onOpen, reduceMotion, stacked }) {
  const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 + index * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      whileHover={reduceMotion ? undefined : { y: -4 }}
      whileTap={{ scale: 0.985 }}
      aria-label={`Open ${c.title}`}
      style={{
        all: "unset", cursor: "pointer", display: "flex", flexDirection: stacked ? "row" : "column", boxSizing: "border-box",
        background: "#fff", borderRadius: LEARN.radius.lg, overflow: "hidden", textAlign: "left",
        border: `2px solid ${LEARN.color.line}`, boxShadow: LEARN.shadow.card, fontFamily: LEARN.font, color: LEARN.color.ink,
        minHeight: stacked ? 150 : undefined, width: "100%",
      }}
    >
      <div style={{ position: "relative", height: stacked ? "auto" : 170, width: stacked ? 140 : "auto", flexShrink: 0, alignSelf: "stretch" }}>
        <LearnImage asset={c.art} fit="cover" style={{ position: "absolute", inset: 0 }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, transparent 45%, ${c.theme.wash} 100%)` }} />
        <img src={LEARN_UI(c.badge)} alt="" aria-hidden="true" onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ position: "absolute", left: 14, bottom: stacked ? 10 : -16, width: stacked ? 64 : 88, height: stacked ? 64 : 88, objectFit: "contain", filter: "drop-shadow(0 8px 14px rgba(11,26,51,0.22))" }} />
      </div>
      <div style={{ padding: stacked ? "16px 16px 16px 18px" : "26px 18px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{c.title}</h3>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: c.theme.deep, background: c.theme.wash, padding: "4px 10px", borderRadius: 999 }}>
            {c.done}/{c.total}
          </span>
        </div>
        {!stacked && <p style={{ margin: 0, fontSize: 14, color: LEARN.color.muted, lineHeight: 1.5 }}>{c.blurb}</p>}
        <div aria-hidden="true" style={{ height: 10, borderRadius: 999, background: "rgba(11,26,51,0.08)", overflow: "hidden" }}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: "100%", borderRadius: 999, background: `linear-gradient(90deg, ${c.theme.accent}, ${c.theme.deep})` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: "auto", paddingTop: 4 }}>
          <span style={{ fontSize: 13.5, color: LEARN.color.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.next}</span>
          <span style={{ ...stickerButton({ bg: c.theme.accent, deep: c.theme.deep, size: "sm" }), display: "inline-block", flexShrink: 0 }}>
            {c.done > 0 ? "Continue" : "Start"}
          </span>
        </div>
      </div>
    </motion.button>
  );
}
